import { LitElement, html, nothing, type PropertyValues } from 'lit';
import styles from './styles.js';
import { property, query } from 'lit/decorators.js';
import type { Placement } from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController } from '../utils/placement.controller.js';

/**
 * uwc-popover
 *
 * Structured click-triggered popover with optional header, footer,
 * and close button. For a free-form panel use uwc-overlay instead.
 *
 * @slot trigger    - Element that opens the popover on click.
 * @slot header     - Optional header area (above content divider).
 * @slot content    - Main body content.
 * @slot footer     - Optional footer area (below content divider).
 *
 * @attr trigger-id         - External trigger element id.
 * @attr placement          - Panel placement. Default: bottom.
 * @attr offset             - Gap px. Default: 8.
 * @attr header             - Plain-text header (alternative to slot="header").
 * @attr show-close-button  - Render ✕ button in the header row.
 * @attr close-on-escape    - Default: true.
 * @attr close-on-outside-click - Default: true.
 * @attr arrow              - Show placement arrow. Default: false.
 *
 * @fires uwc-show
 * @fires uwc-hide
 * @fires uwc-close  - Fired when user clicks the close button.
 *
 * @csspart panel
 * @csspart header
 * @csspart body
 * @csspart footer
 * @csspart close-btn
 * @csspart arrow
 *
 * @cssprop --uwc-popover-bg
 * @cssprop --uwc-popover-border
 * @cssprop --uwc-popover-radius
 * @cssprop --uwc-popover-shadow
 * @cssprop --uwc-popover-z
 * @cssprop --uwc-popover-duration
 * @cssprop --uwc-popover-header-bg
 * @cssprop --uwc-popover-header-color
 */
export class UwcPopover extends LitElement {

  @property({ type: String,  attribute: 'trigger-id'              }) triggerId?: string;
  @property({ type: String                                         }) placement: Placement = 'bottom';
  @property({ type: Number                                         }) offset = 8;
  @property({ type: String                                         }) header?: string;
  @property({ type: Boolean, attribute: 'show-close-button'        }) showCloseButton = false;
  @property({ type: Boolean, attribute: 'close-on-escape'          }) closeOnEscape = true;
  @property({ type: Boolean, attribute: 'close-on-outside-click'   }) closeOnOutsideClick = true;
  @property({ type: Boolean, reflect: true                         }) arrow = false;

  @query('#panel') private _panel!: HTMLElement;
  @query('#arrow') private _arrow!: HTMLElement;

  private readonly _ARROW_HALF = 6;

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panel,

    onTriggerFound: (el) => {
      const handler = (e: Event): void => { e.stopPropagation(); this._pc.toggle(); };
      el.addEventListener('click', handler);
      return () => el.removeEventListener('click', handler);
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => emit(this, 'uwc-show'),
    afterHide: () => emit(this, 'uwc-hide'),

    afterPosition: ({ triggerRect, panelCoords, panelEl }) => {
      if (this.arrow) this._positionArrow(triggerRect, panelCoords, panelEl);
    },
  });

  override firstUpdated(): void {
    this._pc.firstUpdated(
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._pc.updated(
      changed as Map<PropertyKey, unknown>,
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  private _positionArrow(tRect: DOMRect, coords: { top: number; left: number }, panelEl: HTMLElement): void {
    const arrow = this._arrow;
    if (!arrow) return;
    const [side]  = (this.placement).split('-');
    const pRect   = panelEl.getBoundingClientRect();
    const AH      = this._ARROW_HALF;
    const MIN     = 12;

    arrow.style.cssText = '';

    if (side === 'top' || side === 'bottom') {
      const cx  = tRect.left + tRect.width / 2;
      const max = pRect.width - AH * 2 - MIN;
      const al  = Math.max(MIN, Math.min(cx - coords.left - AH, max));
      arrow.style.left = `${al}px`;
      arrow.style[side === 'bottom' ? 'top' : 'bottom'] = `${-AH}px`;
    } else {
      const cy  = tRect.top + tRect.height / 2;
      const max = pRect.height - AH * 2 - MIN;
      const at  = Math.max(MIN, Math.min(cy - coords.top - AH, max));
      arrow.style.top = `${at}px`;
      arrow.style[side === 'right' ? 'left' : 'right'] = `${-AH}px`;
    }
  }

  private _handleClose(e: Event): void {
    this._pc.hide();
    emit(this, 'uwc-close', { originalEvent: e });
  }

  show():   void { this._pc.show(); }
  hide():   void { this._pc.hide(); }
  toggle(): void { this._pc.toggle(); }
  get isOpen(): boolean { return this._pc.open; }

  private _hasHeaderSlot = (): boolean =>
    Array.from(this.children).some(c => c.slot === 'header');

  private _hasFooterSlot = (): boolean =>
    Array.from(this.children).some(c => c.slot === 'footer');

  protected override render() {
    const isExternal = Boolean(this.triggerId);
    const open       = this._pc.open;
    const showHeader = this.header || this._hasHeaderSlot() || this.showCloseButton;
    const showFooter = this._hasFooterSlot();

    return html`
      ${!isExternal
        ? html`<slot name="trigger" part="trigger"></slot>`
        : nothing}

      <div
        id="panel"
        part="panel"
        popover="manual"
        class="panel ${open ? 'is-open' : ''}"
        data-placement=${this.placement}
      >
        ${this.arrow
          ? html`<div id="arrow" part="arrow" class="arrow"></div>`
          : nothing}

        ${showHeader ? html`
          <div part="header" class="header">
            <div class="header-content">
              ${this.header
                ? html`<span class="header-text">${this.header}</span>`
                : html`<slot name="header"></slot>`}
            </div>
            ${this.showCloseButton ? html`
              <button
                part="close-btn"
                class="close-btn"
                aria-label="Close"
                @click=${this._handleClose}
              ><uwc-icon name="x-lg" size="1.15em"></uwc-icon></button>` : nothing}
          </div>` : nothing}

        <div part="body" class="body">
          <slot name="content"></slot>
          <slot></slot>
        </div>

        ${showFooter ? html`
          <div part="footer" class="footer">
            <slot name="footer"></slot>
          </div>` : nothing}
      </div>`;
  }

  static override styles = [styles];
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-popover': UwcPopover; }
}
